import type { Icon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';

import CaretCircleDown from '@/components/Icons/CaretCircleDown';
import DotsSixVertical from '@/components/Icons/DotsSixVertical';

import s from './CollapsibleArea.module.scss';

// TODO(thure): Passing ReactNode as props is deprecated. These should be refactored to use
// render props, compound components, or slots pattern instead. See AGENTS.md for guidance.
type Props = {
  title: React.ReactNode;
  openTitle?: React.ReactNode;
  className?: string;
  Icon?: {
    Component: Icon;
    ComponentOpen?: Icon;
    color?: string;
    size?: number;
  };
  caretPosition?: 'left' | 'right';
  open?: boolean;
  description?: React.ReactNode;
  hideCaret?: boolean;
  CustomIcon?: React.ReactNode;
  draggable?: boolean;
  onlyToggleOnCaret?: boolean;
  clipTitle?: boolean;
};

const CollapsibleArea: React.FC<PropsWithChildren<Props>> = ({
  CustomIcon,
  caretPosition = 'right',
  children,
  className,
  description,
  open,
  title,
  openTitle,
  Icon,
  hideCaret,
  draggable = false,
  onlyToggleOnCaret = false,
  clipTitle = false,
}) => {
  const [isOpen, setIsOpen] = useState(open);
  const [isUserTriggered, setIsUserTriggered] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const toggleListener = (): void => {
      setIsOpen(detailsRef.current?.open);
    };

    const detailsElement = detailsRef.current;
    detailsElement?.addEventListener('toggle', toggleListener);

    return () => {
      detailsElement?.removeEventListener('toggle', toggleListener);
    };
  }, [setIsOpen]);

  useEffect(() => {
    if (isOpen && isUserTriggered && detailsRef.current) {
      detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setIsUserTriggered(false);
    }
  }, [isOpen, isUserTriggered]);

  const handleToggleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsUserTriggered(true);
    detailsRef.current?.click();
  };

  const IconComponent = Icon?.ComponentOpen && isOpen ? Icon.ComponentOpen : Icon?.Component;

  return (
    <details ref={detailsRef} className={classNames(s['collapsible-area'], className)} open={isOpen}>
      <summary
        onClick={(e) => {
          if (onlyToggleOnCaret) {
            e.preventDefault();
          } else {
            e.stopPropagation();
            setIsUserTriggered(true);
          }
        }}
      >
        {caretPosition === 'left' && !hideCaret && (
          <CaretCircleDown weight='fill' size={16} onClick={handleToggleClick} style={{ cursor: 'pointer' }} />
        )}
        <div>
          {draggable && <DotsSixVertical size={20} />}
          {Icon && IconComponent && (
            <IconComponent size={Icon.size || 20} color={Icon.color || 'var(--color-gray-100)'} />
          )}
          {CustomIcon && <div className={s.customIcon}>{CustomIcon}</div>}
          <div className={s.titleAndDescription}>
            <span
              title={clipTitle && typeof title === 'string' ? title : undefined}
              className={clipTitle ? s.ellipsis : undefined}
            >
              {isOpen && openTitle ? openTitle : title}
            </span>
            {description && <span className='caption'>{description}</span>}
          </div>
        </div>
        {caretPosition === 'right' && !hideCaret && (
          <CaretCircleDown weight='fill' size={16} onClick={handleToggleClick} style={{ cursor: 'pointer' }} />
        )}
      </summary>
      {children}
    </details>
  );
};

export default CollapsibleArea;
